import { LOCAL_STORAGE_KEYS } from 'constant/localStorage';
import { getHeapSize } from 'utils/observability/storage';

/**
 * Cache of session-scoped span attributes that are otherwise expensive or
 * repetitive to compute on every span start.
 *
 * WHY: the span processor's `onStart()` runs for EVERY span (one per
 * fetch/XHR/interaction). The pre-disable code read `localStorage` and
 * recomputed heap size there, putting synchronous main-thread work
 * proportional to span volume — a contributor to the jank that fed the
 * telemetry storm. Here those values are computed OFF the hot path (once at
 * init, then on a low-frequency timer and when the tab becomes visible), so
 * `onStart()` can do pure, allocation-free reads via `getLiveContext()`.
 *
 * Identity is POLLED, not pushed, to keep this module decoupled from the auth
 * flow and the router. Trade-off: for up to `REFRESH_MS` after login, spans
 * carry the pre-login 'Unknown*' identity. Acceptable for the internal-only
 * rollout; revisit if precise first-post-login attribution is needed.
 */
type Heap = ReturnType<typeof getHeapSize>;

export interface LiveContext {
  userId: string;
  tenantId: string;
  heap: Heap;
}

const REFRESH_MS = 5000;

const cache: LiveContext = {
  userId: 'UnknownUser',
  tenantId: 'UnknownTenant',
  heap: getHeapSize(),
};

let started = false;

function refresh(): void {
  cache.userId =
    localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ID) ?? 'UnknownUser';
  cache.tenantId =
    localStorage.getItem(LOCAL_STORAGE_KEYS.TENANT) ?? 'UnknownTenant';
  cache.heap = getHeapSize();
}

/** Populate the cache and keep it fresh. Idempotent (HMR / double-init safe). */
export function initLiveContext(): void {
  if (started) {
    return;
  }
  started = true;
  refresh();
  // Low-frequency refresh: identity changes ~once per login; heap drifts slowly.
  setInterval(refresh, REFRESH_MS);
  // Refresh promptly when the user returns to the tab.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      refresh();
    }
  });
}

/** Pure read for the span hot path — no I/O. */
export function getLiveContext(): LiveContext {
  return cache;
}
